import EventEmitter2 from 'eventemitter2';
import environment from '@lib/environment';
import IAppLogger, {
  LogLevel,
  LoggerExceptionFn,
  LoggerFn,
} from './IAppLogger';

export const APP_LOGS_LEVEL_KEY = 'app.logs.level';
export const APP_LOGS_KEYS_KEY = 'app.logs.keys';
const MAX_LATEST = 2000;
const EMITTER_LISTENERS_NUMBER = 30;

type StashedMessage = {
  key: string;
  level?: LogLevel;
  message: any;
  args: any;
  error?: unknown;
  createdAt: number;
};

class AppLogs {
  private readonly _emitter: EventEmitter2 = new EventEmitter2({
    ignoreErrors: true,
  });
  private _loggers: Record<string, IAppLogger> = {};
  private _runtimeLogLevel: number;
  private _levels: Record<string, LogLevel> = {};
  private readonly _originalConsoleDebug: any;
  private readonly _originalConsoleLog: any;
  private readonly _originalConsoleWarn: any;
  private readonly _originalConsoleError: any;
  private readonly _logger: IAppLogger;
  private readonly _stashedMessages: Array<StashedMessage> = [];

  constructor() {
    this._logger = this.get('app');
    this._emitter.setMaxListeners(EMITTER_LISTENERS_NUMBER);

    // log level determined by localstorage key APP_LOGS_KEYS_KEY
    // defaults to the log level for the env if not set
    this._runtimeLogLevel = this.logLevelToNumber(this.getDefaultLevelForEnv());

    // overide console
    this._originalConsoleDebug = console.debug;
    this._originalConsoleLog = console.log;
    this._originalConsoleWarn = console.warn;
    this._originalConsoleError = console.error;

    // for now we trap all logs to 'Unknown' logger
    // but still let the original console log
    const unknownLogger = this.get('Unknown', 'debug');

    console.log = console.info = (...args: any[]) => {
      unknownLogger.info(args[0], ...args.slice(1));
    };
    console.debug = (...args: any[]) => {
      unknownLogger.debug(args[0], ...args.slice(1));
    };
    console.warn = (...args: any[]) => {
      unknownLogger.warn(args[0], ...args.slice(1));
    };
    console.error = (...args: any) => {
      unknownLogger.error(args[0], ...args.slice(1));
    };

    // manage key levels in localstorage
    this.setupKeyStorage();
  }

  public get(key: string, level?: LogLevel): IAppLogger {
    key = key.toLowerCase();
    if (level && !this._levels[key]) {
      this._levels[key] = level;
    }
    if (!this._loggers[key]) {
      this._loggers[key] = {
        debug: this.createLoggerFn(key, 'debug'),
        info: this.createLoggerFn(key, 'info'),
        warn: this.createLoggerFn(key, 'warn'),
        error: this.createLoggerFn(key, 'error'),
        exception: this.createLoggerExceptionFn(key),
        on: this.on.bind(this),
        onAny: this.onAny.bind(this),
        off: this.off.bind(this),
      };
    }
    return this._loggers[key];
  }

  public get latest(): Array<StashedMessage> {
    return this._stashedMessages;
  }

  public on(
    level: LogLevel,
    listener: (key: string, message: any, args: any[], error?: unknown) => void,
  ): void {
    this._emitter.on(level, listener);
  }

  public onAny(
    listener: (
      level: string | string[],
      key: string,
      message: any,
      args: any[],
      error?: unknown,
    ) => void,
  ): void {
    this._emitter.onAny(listener);
  }

  public off(
    level: LogLevel,
    listener: (key: string, message: any, args: any[]) => void,
  ): void {
    this._emitter.off(level, listener);
  }

  private createLoggerFn(key: string, level: LogLevel): LoggerFn {
    return (message: any, ...optionalParams: any[]) => {
      // don't log these messages
      if (message === '@log-service/error') {
        return;
      }

      // save latest event
      this.stashEvent(key, level, message, optionalParams);

      // emit log event
      this._emitter.emit(level, key.toLowerCase(), message, optionalParams);

      // log to console if allowed
      if (this.shouldLog(key, level)) {
        const ts = new Date().toISOString();
        let logFn = this._originalConsoleLog;
        if (level === 'debug') {
          logFn = this._originalConsoleDebug;
        } else if (level === 'warn') {
          logFn = this._originalConsoleWarn;
        } else if (level === 'error') {
          logFn = this._originalConsoleError;
        }
        logFn(
          `[${ts}] ${level}(${key.toLowerCase()}):`,
          message,
          ...optionalParams,
        );
      }
    };
  }

  private createLoggerExceptionFn(key: string): LoggerExceptionFn {
    return (err: unknown, message: any, ...optionalParams: any[]) => {
      // save latest event
      this.stashEvent(key, 'error', message, optionalParams);

      // emit log event
      this._emitter.emit(
        'error',
        key.toLowerCase(),
        message,
        optionalParams,
        err,
      );

      if (this.shouldLog(key, 'error')) {
        const ts = new Date().toISOString();
        this._originalConsoleError(
          `[${ts}] exception(${key.toLowerCase()}): ${message}`,
          optionalParams,
          err,
        );
      }
    };
  }

  private stashEvent(
    key: string,
    level: LogLevel,
    message: any,
    args: any,
    error?: unknown,
  ) {
    // track last MAX_LATEST messages
    this._stashedMessages.push({
      key,
      level,
      message,
      args,
      error,
      createdAt: Date.now(),
    });
    if (this._stashedMessages.length > MAX_LATEST) {
      this._stashedMessages.shift();
    }
  }

  private logLevelToNumber(level: LogLevel | undefined): number {
    switch (level) {
      case 'debug':
        return 1;
      case 'info':
        return 2;
      case 'warn':
        return 3;
      case 'error':
        return 4;
      default:
        return 5;
    }
  }

  /**
   * Determines whether a log event should be logged based on its key and level.
   *
   * This takes into account the log level set when the log key was defined
   * and the runtime log level. The runtime log level is set by a person
   * in local storage and overrides the default log level for the environment
   * and log itself.
   *
   * @param key - The key associated with the log event.
   * @param level - The log level of the event.
   * @returns `true` if the event should be logged, `false` otherwise.
   */
  private shouldLog(key: string, level: LogLevel): boolean {
    // get the level defined for the log key if it was set, otherwise use
    // the runtime log level
    const logKeyLevel = this._levels[key.toLowerCase()]
      ? this.logLevelToNumber(this._levels[key.toLowerCase()])
      : this._runtimeLogLevel;

    // the level the current event wants to use
    const desiredLogLevel = this.logLevelToNumber(level);

    // if the desired log level is less than the log level for the key
    // we don't show the event in console
    if (desiredLogLevel < logKeyLevel) {
      return false;
    }

    // if the desired log level is less than the runtime log level
    // we don't show the event in console
    if (desiredLogLevel < this._runtimeLogLevel) {
      return false;
    }
    return true;
  }

  private getDefaultLevelForEnv(): LogLevel {
    switch (environment.RUNTIME_ENV) {
      case 'qa':
        return 'debug';
      case 'stage':
        return 'info';
      case 'prod':
        return 'error';
      case 'local':
        return 'debug';
      default:
        return 'info';
    }
  }

  private setupKeyStorage() {
    this.initKeyStorage();
    this.readKeyLevelFromStorage();
    this.readKeysFromStorage();
    window.addEventListener('storage', this.handleKeyStorageChange);
  }

  private initKeyStorage() {
    if (!localStorage.getItem(APP_LOGS_KEYS_KEY)) {
      localStorage.setItem(
        APP_LOGS_KEYS_KEY,
        JSON.stringify({ example: 'info' }),
      );
    }
    if (!localStorage.getItem(APP_LOGS_LEVEL_KEY)) {
      localStorage.setItem(APP_LOGS_LEVEL_KEY, '');
    }
  }

  private readonly handleKeyStorageChange = (event: StorageEvent) => {
    if (event.key === APP_LOGS_KEYS_KEY) {
      this.readKeysFromStorage();
      this._logger.info('log levels updated', this._levels);
    } else if (event.key === APP_LOGS_LEVEL_KEY && event.newValue !== null) {
      this.readKeyLevelFromStorage();
      this._logger.info('log level updated', this._runtimeLogLevel);
    }
  };

  private readKeyLevelFromStorage() {
    const level = localStorage.getItem(APP_LOGS_LEVEL_KEY);
    if (level !== null && level !== '') {
      this._runtimeLogLevel = this.logLevelToNumber(level as LogLevel);
    } else {
      this._runtimeLogLevel = this.logLevelToNumber(
        this.getDefaultLevelForEnv(),
      );
    }
  }

  private readKeysFromStorage() {
    const keys = JSON.parse(localStorage.getItem(APP_LOGS_KEYS_KEY) ?? '{}');

    // update our levels
    for (const key in keys) {
      this._levels[key] = keys[key];
    }
  }
}

export default AppLogs;
